/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { Timestamp } from '@amazon/vinyl-util'
import { isAmazonDomain } from '@/territory'

declare global {
    interface Window {
        amznMusic?: {
            /** The boot config rendered into the served HTML (see {@link readInlineBootConfig}). */
            appConfig?: unknown
            /** Epoch ms the server minted `appConfig`, stamped at render time. */
            appConfigIssuedAt?: unknown
            /** In-flight `/config.json` POST started by the HTML, resolving `{config, issuedAt}`. */
            appConfigPromise?: unknown
            // GreenHornet marks a cookie-auth build by setting this before boot.
            isCookieAuthEnabled?: boolean
        }
    }
}

/**
 * The CSRF triple the server mints alongside the session; sent back on
 * authenticated requests as `csrf-rnd`/`csrf-token`/`csrf-ts` headers (matches
 * GreenHornet's config.json shape and media-gql-client's CSRFTokenConfig).
 */
export interface CsrfConfig {
    rnd: string
    token: string
    ts: string
}

export interface TokenConfig {
    accessToken: string
    expiresAt: Timestamp
}

/** AuthPortal's `/config.json` body, narrowed to the fields amu reads. */
export interface BootConfig {
    accessToken?: string
    accessTokenExpiresIn?: number
    csrf?: CsrfConfig
    customerId?: string
    isProfileCustomer?: boolean
    montanaCsrf?: string
}

/** A boot config resolved onto absolute time, ready to commit to the auth controller. */
export interface BootSession {
    csrf: CsrfConfig | null
    customerId: string | null
    montanaCsrf: string | null
    /** The account has no listener profile chosen yet, so AuthPortal withholds its token. */
    needsProfilePicker: boolean
    token: TokenConfig | null
}

/** Marks a boot config answer that sent the browser elsewhere, so the app must not start. */
export const BOOT_REDIRECTED = Symbol('bootRedirected')

/** Never hand a token this close to lapsing downstream — it pushes bad auth to the wire. */
export const EXPIRY_BUFFER_MS = 30 * 1000

// An inline config older than this is a stale document (a cache that ignored
// `no-store`, a restored session), so its session claims are refetched.
const MAX_INLINE_AGE_SECONDS = 5 * 60

/** Resolves a boot config's relative token expiry against the time the server minted it. */
export function toBootSession(config: BootConfig, issuedAt: Timestamp): BootSession {
    const expiresIn = config.accessTokenExpiresIn
    return {
        csrf: config.csrf ?? null,
        customerId: nonEmpty(config.customerId),
        montanaCsrf: nonEmpty(config.montanaCsrf),
        needsProfilePicker: Boolean(config.customerId) && config.isProfileCustomer === false,
        token:
            config.accessToken && expiresIn != null
                ? { accessToken: config.accessToken, expiresAt: issuedAt + expiresIn * 1000 }
                : null,
    }
}

/**
 * The boot session the AuthPortal origin rendered into the served HTML as
 * `window.amznMusic.appConfig` (GreenHornet's global), or `null` when the
 * document carries none, carries a malformed one, or is stale enough that its
 * session claims can't be trusted — the caller then POSTs `/config.json` itself.
 *
 * Resolving from the document is what keeps the first paint off the network: the
 * HTML server already knows the session, so the client needn't ask again.
 */
export function readInlineBootConfig(): BootSession | typeof BOOT_REDIRECTED | null {
    const { appConfig, appConfigIssuedAt: issuedAt } = window.amznMusic ?? {}
    if (followBootRedirect(appConfig)) return BOOT_REDIRECTED
    return resolveBootSession(appConfig, issuedAt)
}

/**
 * The session from the `/config.json` POST the HTML started before the bundle
 * loaded, or `null` when none was started, it failed, or its answer fails the
 * same checks as the inline config — the caller then POSTs itself. One-shot:
 * the promise is cleared so a retry never replays a settled answer.
 */
export async function takeEarlyBootConfig(): Promise<BootSession | typeof BOOT_REDIRECTED | null> {
    const music = window.amznMusic
    const pending = music?.appConfigPromise
    if (!music || !(pending instanceof Promise)) return null
    delete music.appConfigPromise
    try {
        const answer: unknown = await pending
        if (!isRecord(answer)) return null
        if (followBootRedirect(answer.config)) return BOOT_REDIRECTED
        return resolveBootSession(answer.config, answer.issuedAt)
    } catch {
        return null
    }
}

/**
 * Sends the browser to the `redirectUrl` of a boot config answer, as GH does, and reports whether
 * it did. Unlike GH, only this site or an Amazon Music or retail host is followed, so a tampered
 * answer cannot turn the app into an open redirect.
 */
export function followBootRedirect(appConfig: unknown): boolean {
    if (!isRecord(appConfig)) return false
    const target = safeRedirectTarget(appConfig.redirectUrl)
    if (!target) return false
    window.location.href = target
    return true
}

function safeRedirectTarget(redirectUrl: unknown): string | null {
    if (typeof redirectUrl !== 'string' || !redirectUrl) return null
    try {
        const url = new URL(redirectUrl, window.location.href)
        const sameSite = url.origin === window.location.origin
        const amazonOwned = url.protocol === 'https:' && isAmazonDomain(url.href)
        return sameSite || amazonOwned ? url.href : null
    } catch {
        return null
    }
}

function resolveBootSession(appConfig: unknown, issuedAt: unknown): BootSession | null {
    if (!isRecord(appConfig) || typeof issuedAt !== 'number') return null
    const now = Date.now()
    if (!Number.isFinite(issuedAt) || now - issuedAt > MAX_INLINE_AGE_SECONDS * 1000) return null
    const config = parseBootConfig(appConfig)
    if (!config) return null
    const session = toBootSession(config, issuedAt)
    if (session.token && session.token.expiresAt < now + EXPIRY_BUFFER_MS) return null
    return session
}

// Any field of the wrong type makes the whole payload untrusted: a half-read
// session would boot with a customer id but no csrf, or a token with no expiry.
function parseBootConfig(config: Record<string, unknown>): BootConfig | null {
    const { accessToken, accessTokenExpiresIn, csrf, customerId, isProfileCustomer, montanaCsrf } =
        config
    if (!isOptionalString(customerId) || !isOptionalString(montanaCsrf)) return null
    if (!isOptionalString(accessToken) || !isOptionalBoolean(isProfileCustomer)) return null
    if (!isOptionalCsrfConfig(csrf)) return null
    const expiresIn = toPositiveNumber(accessTokenExpiresIn)
    if (accessToken && expiresIn == null) return null
    return {
        ...(customerId != null ? { customerId } : null),
        ...(montanaCsrf != null ? { montanaCsrf } : null),
        ...(isProfileCustomer != null ? { isProfileCustomer } : null),
        ...(csrf != null ? { csrf } : null),
        ...(accessToken && expiresIn != null
            ? { accessToken, accessTokenExpiresIn: expiresIn }
            : null),
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isOptionalString(value: unknown): value is string | undefined {
    return value == null || typeof value === 'string'
}

function isOptionalBoolean(value: unknown): value is boolean | undefined {
    return value == null || typeof value === 'boolean'
}

function isOptionalCsrfConfig(value: unknown): value is CsrfConfig | undefined {
    if (value == null) return true
    return (
        isRecord(value) &&
        typeof value.rnd === 'string' &&
        typeof value.token === 'string' &&
        typeof value.ts === 'string'
    )
}

// AuthPortal reports the lifetime in seconds; a server rendering it into the
// document may carry it as a string.
function toPositiveNumber(value: unknown): number | null {
    if (typeof value !== 'number' && typeof value !== 'string') return null
    const seconds = Number(value)
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null
}

function nonEmpty(value: string | undefined): string | null {
    return value ? value : null
}
